import * as React from 'react';
import { Form, DatePickerRange, TextInput, Select, CustomSubmitButton, Checkbox, ClearFormButton, CustomSubmitButtonType } from "soprasteria-ui-library";
import { useAuth } from '../../../App';
import { OrthodonticReferralSearchApi } from '../../../api/OrthodonticReferralSearchApi';
import { IOrthodonticReferralSearchParams, IOrthodonticReferralDecision } from '../../../models/IOrthodonticReferral';
import { IListItem } from 'soprasteria-ui-library/dist/models/IFormInput';
import { CareProviderDropdown } from '../../CareProviderDropdown/CareProviderDropdown';
import { ICareProvider } from '../../../models/ICareProvider';
import { UserContext, isInRole } from '../../../contexts/UserContext';
import { ALLMÄNTANDVÅRDBAS, LIBRETTOADMIN, URVALSTANDLÄKAREBAS, VÅRDANSVARIG } from '../../../utils/RoleConstants';
import { OrthodonticReferralDecisionDropdown } from './OrthodonticReferralDecisionDropdown';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileExcel } from '@fortawesome/free-solid-svg-icons';

interface IOrthodonticReferralSearchForm {
    preSetSearchParams?: IOrthodonticReferralSearchParams;
    searchOrthodonticReferrals: (data: IOrthodonticReferralSearchParams) => void;
    searchOrthodonticReferralsToExcel: (data: IOrthodonticReferralSearchParams) => void;
}

export const OrthodonticReferralSearchForm: React.FunctionComponent<IOrthodonticReferralSearchForm> = ({ searchOrthodonticReferrals, searchOrthodonticReferralsToExcel, preSetSearchParams }) => {

    const auth = useAuth();
    const api = new OrthodonticReferralSearchApi(auth);
    const [orthodonticReferralStatuses, setOrthodonticReferralStatuses] = React.useState<IListItem[]>();
    const [referrerCareProviders, setReferrerCareProviders] = React.useState<ICareProvider[]>();
    const [treatmentResponsibleCareProviders, setTreatmentResponsibleCareProviders] = React.useState<ICareProvider[]>();

    const [referrerUmbrellaOrganisations, setReferrerUmbrellaOrganisations] = React.useState<ICareProvider[]>();
    const [isFetchingReferrerUmbrellaOrganisations, setIsFetchingReferrerUmbrellaOrganisations] = React.useState<boolean>(false);
    const [selectedReferrerUmbrellaOrganisationId, setSelectedReferrerUmbrellaOrganisationId] = React.useState<number>();
    const [resetReferrerCareProviderValue, doResetReferrerCareProviderValue] = React.useState<boolean>(false);

    const [treatmentResponsibleUmbrellaOrganisations, setTreatmentResponsibleUmbrellaOrganisations] = React.useState<ICareProvider[]>();
    const [isFetchingTreatmentResponsibleUmbrellaOrganisations, setIsFetchingTreatmentResponsibleUmbrellaOrganisations] = React.useState<boolean>(false);
    const [selectedTreatmentResponsibleUmbrellaOrganisationId, setSelectedTreatmentResponsibleUmbrellaOrganisationId] = React.useState<number>();
    const [resetTreatmentResponsibleCareProviderValue, doResetTreatmentResponsibleCareProviderValue] = React.useState<boolean>(false);

    const [consultantCareProviders, setConsultantCareProviders] = React.useState<ICareProvider[]>();
    const [orthodonticReferralDecisions, setOrthodonticReferralDecisions] = React.useState<IOrthodonticReferralDecision[]>();
    const [onlyShowReferralsWithRequestedPatientViewing, setOnlyShowReferralsWithRequestedPatientViewing] = React.useState<boolean>(false);
    const [onlyShowOrthodonticReferralsWithUnreadAnswer, setOnlyShowOrthodonticReferralsWithUnreadAnswer] = React.useState<boolean>(false);
    const [onlyShowReferralsWithComplement, setOnlyShowReferralsWithComplement] = React.useState<boolean>(false);
    const [showOrthodonticReferralsToShare, setShowOrthodonticReferralsToShare] = React.useState<boolean>(false);
    const userInfo = React.useContext(UserContext);
    const hasRoleAllmantandvard = isInRole(ALLMÄNTANDVÅRDBAS, userInfo.roles ? userInfo.roles : []);
    const hasRoleKonsult = isInRole(URVALSTANDLÄKAREBAS, userInfo.roles ? userInfo.roles : []);
    const hasRoleParaply = isInRole(VÅRDANSVARIG, userInfo.roles ? userInfo.roles : []);
    const hasRoleSystemAdministrator = isInRole(LIBRETTOADMIN, userInfo.roles ? userInfo.roles : []);
    const abortController = new AbortController();

    React.useEffect(() => {
        getReferrerUmbrellaOrganisations();
        getTreatmentResponsibleUmbrellaOrganisations();
        getOrthodonticReferralStatuses();
        getOrthodonticReferralDecisions();
        getConsultantCareProviders();

        return () => {
            abortController.abort();
        }
    }, [])

    React.useEffect(() => {
        if (preSetSearchParams?.onlyShowReferralsWithRequestedPatientViewing) {
            setOnlyShowReferralsWithRequestedPatientViewing(true);
        }
        if (preSetSearchParams?.showOrthodonticReferralsToShare) {
            setShowOrthodonticReferralsToShare(true);
        }
        if (preSetSearchParams?.onlyShowOrthodonticReferralsWithUnreadAnswer) {
            setOnlyShowOrthodonticReferralsWithUnreadAnswer(true);
        }
        if (preSetSearchParams?.onlyShowReferralsWithComplement) {
            setOnlyShowReferralsWithComplement(true);
        }
    }, [preSetSearchParams])

    const getOrthodonticReferralStatuses = () => {
        api.getOrthodonticReferralStatuses((statuses) => {
            const statusListItems = statuses.map<IListItem>((status) => ({ text: status.description, value: status.statusId.toString() }));
            setOrthodonticReferralStatuses(statusListItems);
        });
    }

    const getOrthodonticReferralDecisions = () => {
        api.getOrthodonticReferralDecisions((decisions) => setOrthodonticReferralDecisions(decisions))
    }

    const getConsultantCareProviders = () => {
        api.getConsultantCareProviders((careProviders) => setConsultantCareProviders(careProviders))
    }

    const getReferrerUmbrellaOrganisations = () => {
        setIsFetchingReferrerUmbrellaOrganisations(true);
        api.getReferrerUmbrellaOrganisations((umbrellaOrganisations) => {
            let umbrellaOrganisationIdToUse;
            if (preSetSearchParams?.referrerUmbrellaOrganisationId) {
                umbrellaOrganisationIdToUse = parseInt(preSetSearchParams.referrerUmbrellaOrganisationId.toString());
                getReferrerCareProvidersByUmbrellaOrganisationId(umbrellaOrganisationIdToUse, umbrellaOrganisations);
            } else if (!hasRoleSystemAdministrator && umbrellaOrganisations.length === 1) {
                umbrellaOrganisationIdToUse = umbrellaOrganisations[0].careProviderId;
                setSelectedReferrerUmbrellaOrganisationId(umbrellaOrganisations[0].careProviderId);
                getReferrerCareProvidersByUmbrellaOrganisationId(umbrellaOrganisationIdToUse, umbrellaOrganisations);
            }
            setReferrerUmbrellaOrganisations(umbrellaOrganisations);
        }, undefined, () => {
            setIsFetchingReferrerUmbrellaOrganisations(false);
        });
    }

    const getReferrerCareProvidersByUmbrellaOrganisationId = (umbrellaOrganisationId: number, umbrellaOrganisationsAlt?: ICareProvider[]) => {
        setIsFetchingReferrerUmbrellaOrganisations(true);

        api.getReferrerCareProvidersByUmbrellaOrganisationId(umbrellaOrganisationId, (careProviders) => {
            if (careProviders.length !== 0) {
                setReferrerCareProviders(careProviders);
            } else {
                setReferrerCareProviders(undefined);
            }
            setIsFetchingReferrerUmbrellaOrganisations(false);
        }, undefined, () => { });
    }

    const onValueChangeReferrerUmbrellaOrganisations = (selectedUmbrellaOrganisation: string | undefined) => {
        if (selectedUmbrellaOrganisation && selectedUmbrellaOrganisation !== "") {
            getReferrerCareProvidersByUmbrellaOrganisationId(parseInt(selectedUmbrellaOrganisation));
            setSelectedReferrerUmbrellaOrganisationId(parseInt(selectedUmbrellaOrganisation));
        }
        else {
            setSelectedReferrerUmbrellaOrganisationId(undefined);
            setReferrerCareProviders([]);
        }
        doResetReferrerCareProviderValue(prev => !prev);
    }

    const getReferrerUmbrellaOrganisationDefaultValue = () => {
        if (referrerUmbrellaOrganisations && preSetSearchParams?.referrerUmbrellaOrganisationId) {
            return preSetSearchParams.referrerUmbrellaOrganisationId.toString();
        }

        if (selectedReferrerUmbrellaOrganisationId) {
            return selectedReferrerUmbrellaOrganisationId.toString();
        }

        if (referrerUmbrellaOrganisations?.length === 1) {
            return referrerUmbrellaOrganisations[0].careProviderId.toString();
        }

        return undefined;
    }

    const getReferrerCareProviderDefaultValue = () => {
        if (referrerCareProviders) {
            const preSetCareProviderIdCasted = preSetSearchParams?.referrerCareProviderId
                ? parseInt(preSetSearchParams.referrerCareProviderId.toString())
                : undefined;

            const preSetCareProviderIdExistInCareProviders = preSetCareProviderIdCasted !== undefined
                ? referrerCareProviders.some((careProvider) => careProvider.careProviderId === preSetCareProviderIdCasted)
                : undefined;

            if (referrerCareProviders && preSetCareProviderIdExistInCareProviders) {
                return preSetSearchParams?.referrerCareProviderId?.toString();
            }

            if (referrerCareProviders && referrerCareProviders.length === 1 && referrerCareProviders[0] !== undefined) {
                return referrerCareProviders[0].careProviderId.toString();
            }
        }

        return undefined;
    }

    const getTreatmentResponsibleUmbrellaOrganisations = () => {
        setIsFetchingTreatmentResponsibleUmbrellaOrganisations(true);
        api.getTreatmentResponsibleUmbrellaOrganisations((umbrellaOrganisations) => {
            let umbrellaOrganisationIdToUse;
            if (preSetSearchParams?.treatmentResponsibleUmbrellaOrganisationId) {
                umbrellaOrganisationIdToUse = parseInt(preSetSearchParams?.treatmentResponsibleUmbrellaOrganisationId.toString());
                setSelectedTreatmentResponsibleUmbrellaOrganisationId(umbrellaOrganisationIdToUse);
                getTreatmentResponsibleCareProvidersByUmbrellaOrganisationId(umbrellaOrganisationIdToUse, umbrellaOrganisations);
            } else if (!hasRoleSystemAdministrator && umbrellaOrganisations.length === 1) {
                umbrellaOrganisationIdToUse = umbrellaOrganisations[0].careProviderId;
                setSelectedTreatmentResponsibleUmbrellaOrganisationId(umbrellaOrganisations[0].careProviderId);
                getTreatmentResponsibleCareProvidersByUmbrellaOrganisationId(umbrellaOrganisationIdToUse, umbrellaOrganisations);
            }

            setTreatmentResponsibleUmbrellaOrganisations(umbrellaOrganisations);
        }, undefined, () => {
            setIsFetchingTreatmentResponsibleUmbrellaOrganisations(false);
        });
    }

    const getTreatmentResponsibleCareProvidersByUmbrellaOrganisationId = (umbrellaOrganisationId: number, umbrellaOrganisationsAlt?: ICareProvider[]) => {
        setIsFetchingTreatmentResponsibleUmbrellaOrganisations(true);

        api.getTreatmentResponsibleCareProviderByUmbrellaOrganisationId(umbrellaOrganisationId, (careProviders) => {
            if (careProviders.length !== 0) {
                setTreatmentResponsibleCareProviders(careProviders);
            } else {
                setTreatmentResponsibleCareProviders(undefined);
            }
            setIsFetchingTreatmentResponsibleUmbrellaOrganisations(false);
        }, undefined, () => { });
    }

    const onValueChangeTreatmentResponsibleUmbrellaOrganisations = (selectedUmbrellaOrganisation: string | undefined) => {
        if (selectedUmbrellaOrganisation && selectedUmbrellaOrganisation !== "") {
            getTreatmentResponsibleCareProvidersByUmbrellaOrganisationId(parseInt(selectedUmbrellaOrganisation));
            setSelectedTreatmentResponsibleUmbrellaOrganisationId(parseInt(selectedUmbrellaOrganisation));
        }
        else {
            setSelectedTreatmentResponsibleUmbrellaOrganisationId(undefined);
            setTreatmentResponsibleCareProviders([]);
        }
        doResetTreatmentResponsibleCareProviderValue(prev => !prev);
    }

    const getTreatmentResponsibleUmbrellaOrganisationDefaultValue = () => {
        if (treatmentResponsibleUmbrellaOrganisations && preSetSearchParams?.treatmentResponsibleUmbrellaOrganisationId) {
            return preSetSearchParams.treatmentResponsibleUmbrellaOrganisationId.toString();
        }

        if (selectedTreatmentResponsibleUmbrellaOrganisationId) {
            return selectedTreatmentResponsibleUmbrellaOrganisationId.toString();
        }

        if (treatmentResponsibleUmbrellaOrganisations?.length === 1) {
            return treatmentResponsibleUmbrellaOrganisations[0].careProviderId.toString();
        }

        return undefined;
    }

    const getTreatmentResponsibleCareProviderDefaultValue = () => {
        if (treatmentResponsibleCareProviders) {
            const preSetCareProviderIdCasted = preSetSearchParams?.treatmentResponsibleCareProviderId
                ? parseInt(preSetSearchParams.treatmentResponsibleCareProviderId.toString())
                : undefined;

            const preSetCareProviderIdExistInCareProviders = preSetCareProviderIdCasted !== undefined
                ? treatmentResponsibleCareProviders.some((careProvider) => careProvider.careProviderId === preSetCareProviderIdCasted)
                : undefined;

            if (treatmentResponsibleCareProviders && preSetCareProviderIdExistInCareProviders) {
                return preSetSearchParams?.treatmentResponsibleCareProviderId?.toString();
            }

            if (treatmentResponsibleCareProviders && treatmentResponsibleCareProviders.length === 1 && treatmentResponsibleCareProviders[0] !== undefined) {
                return treatmentResponsibleCareProviders[0].careProviderId.toString();
            }
        }

        return undefined;
    }

    const renderOnlyShowReferralsWithRequestedPatientViewing = () => {
        return (
            <>
                {
                    hasRoleAllmantandvard || hasRoleKonsult ?
                        <Checkbox name="onlyShowReferralsWithRequestedPatientViewing"
                            label="Visa endast remisser där patient ska kallas till visning"
                            checked={onlyShowReferralsWithRequestedPatientViewing}
                            defaultValue="false"
                            value="true" />
                        : null
                }
            </>
        )
    }

    const getConsultantDefaultValue = () => {
        if (hasRoleKonsult && !hasRoleParaply && consultantCareProviders && consultantCareProviders.length === 1) {
            return consultantCareProviders[0].careProviderId.toString();
        }
        if (preSetSearchParams?.consultantCareProviderId) {
            return preSetSearchParams.consultantCareProviderId.toString();
        }

        return undefined;
    }

    return (
        <section>
            <Form onSubmit={searchOrthodonticReferrals}>
                <div className="row">
                    <div className="col-6">
                        <DatePickerRange label="Datum" name="sentDateFrom" nameSecondary="sentDateTo" inlineLabel
                            value={preSetSearchParams?.sentDateFrom ? new Date(preSetSearchParams.sentDateFrom) : undefined}
                            valueSecondary={preSetSearchParams?.sentDateTo ? new Date(preSetSearchParams.sentDateTo) : undefined} />
                        <TextInput name="orthodonticReferralNumber" label="Remiss-ID" inlineLabel defaultValue={preSetSearchParams?.orthodonticReferralNumber} />
                        <OrthodonticReferralDecisionDropdown label="Beslut" decisions={orthodonticReferralDecisions ?? []} name="orthodonticReferralDecisionIds" isLoading={orthodonticReferralDecisions === undefined} isMultiple
                            defaultValue={preSetSearchParams?.orthodonticReferralDecisionIds?.map((decision) => { return decision.toString() })} />
                        <Select name="orthodonticReferralStatusId" label="Status" placeholder="Välj en status" inlineLabel isLoading={orthodonticReferralStatuses === undefined} selectedValue={preSetSearchParams?.orthodonticReferralStatusId?.toString()} options={orthodonticReferralStatuses ?? []} isClearable />
                        {
                            hasRoleAllmantandvard ?
                                <>
                                    <Checkbox name="showOrthodonticReferreralsWherePatientIsNotListedOnCareProvider" id="showOrthodonticReferreralsWherePatientIsNotListedOnCareProvider"
                                        label={!hasRoleParaply ? "Visa ortodontiremisser där patient inte längre är listad på mottagningen" : "Visa aktiva ortodontiremisser där patient inte är listad på någon av mina vårdgivare"}
                                        checked={preSetSearchParams?.showOrthodonticReferreralsWherePatientIsNotListedOnCareProvider ?? false} value="true" onChange={(val) => setShowOrthodonticReferralsToShare(val)} />
                                    <Checkbox name="onlyShowOrthodonticReferralsWithUnreadAnswer" id="onlyShowOrthodonticReferralsWithUnreadAnswer" label="Visa bara ortodontiremisser med remissvar ej läst" value="true"
                                        checked={onlyShowOrthodonticReferralsWithUnreadAnswer} onChange={(changedValue) => setOnlyShowOrthodonticReferralsWithUnreadAnswer(changedValue)} />
                                </> : null

                        }
                        {
                            showOrthodonticReferralsToShare && !hasRoleParaply ?
                                <>
                                    <Checkbox name="showOrthodonticReferralsToShare" id="showOrthodonticReferralsToShare" label="Visa ortodontiremisser som kan delas"
                                        checked={preSetSearchParams?.showOrthodonticReferralsToShare ?? false} value="true" />

                                </> : null
                        }
                        {
                            hasRoleKonsult ?
                                <>
                                    <Checkbox name="onlyShowOrthodonticReferralsWithTreatmentStartedInAnotherCounty" id="onlyShowOrthodonticReferralsWithTreatmentStartedInAnotherCounty" label="Visa endast ortodontiremisser där tandreglering startats utanför Region Stockholm"
                                        checked={preSetSearchParams?.onlyShowOrthodonticReferralsWithTreatmentStartedInAnotherCounty ?? false} value="true" />
                                </> : null
                        }
                        {
                            hasRoleKonsult ?
                                <>
                                    <Checkbox name="onlyShowReferralsWithComplement" id="onlyShowReferralsWithComplement" label="Visa endast kompletterade remisser"
                                        checked={onlyShowReferralsWithComplement} onChange={(changedValue) => setOnlyShowReferralsWithComplement(changedValue)} value="true" />
                                </> : null
                        }
                    </div>
                    <div className="col-6">

                        <CareProviderDropdown
                            label="Remittent vårdgivare"
                            name="referrerUmbrellaOrganisationId"
                            placeholder="Välj vårdgivare"
                            isLoading={isFetchingReferrerUmbrellaOrganisations}
                            careProviders={referrerUmbrellaOrganisations}
                            onValueChange={onValueChangeReferrerUmbrellaOrganisations}
                            defaultValue={getReferrerUmbrellaOrganisationDefaultValue()}
                            isClearable={referrerUmbrellaOrganisations?.length !== 1}
                            showOrgNum
                        />

                        <CareProviderDropdown
                            label={"Remittent mottagning"}
                            name="referrerCareProviderId"
                            isLoading={referrerCareProviders === undefined}
                            careProviders={referrerCareProviders}
                            defaultValue={getReferrerCareProviderDefaultValue()}
                            isClearable={referrerUmbrellaOrganisations?.length !== 1}
                            showClinicId
                            resetValue={resetReferrerCareProviderValue}

                        />
                        <CareProviderDropdown
                            name="consultantCareProviderId"
                            label="Urvalstandläkare"
                            isLoading={consultantCareProviders === undefined}
                            careProviders={consultantCareProviders}
                            defaultValue={getConsultantDefaultValue()}
                        />
                        <CareProviderDropdown
                            label="Vårdansvarig vårdgivare"
                            name="treatmentResponsibleUmbrellaOrganisationId"
                            placeholder="Välj vårdgivare"
                            isLoading={isFetchingTreatmentResponsibleUmbrellaOrganisations}
                            careProviders={treatmentResponsibleUmbrellaOrganisations}
                            defaultValue={getTreatmentResponsibleUmbrellaOrganisationDefaultValue()}
                            onValueChange={onValueChangeTreatmentResponsibleUmbrellaOrganisations}
                            isClearable={treatmentResponsibleUmbrellaOrganisations?.length !== 1}
                            showOrgNum
                        />
                        <CareProviderDropdown
                            name="treatmentResponsibleCareProviderId"
                            label="Vårdansvarig mottagning"
                            isLoading={treatmentResponsibleCareProviders === undefined}
                            careProviders={treatmentResponsibleCareProviders}
                            defaultValue={getTreatmentResponsibleCareProviderDefaultValue()}
                            isClearable={treatmentResponsibleUmbrellaOrganisations?.length !== 1}
                            showClinicId
                            resetValue={resetTreatmentResponsibleCareProviderValue}
                        />
                        <Checkbox name="includeArchivedReferrals" id="includeArchivedReferrals" label="Inkludera ortodontiremisser som avslutats eller återkallats för mer än tre månader sedan"
                            checked={preSetSearchParams?.includeArchivedReferrals ?? false} value="true" />
                        {
                            renderOnlyShowReferralsWithRequestedPatientViewing()
                        }
                    </div>
                    <div className="col-12">
                        <div className="text-right panel">
                            <span className="d-none">
                                <CustomSubmitButton>Sök</CustomSubmitButton>
                            </span>
                            <CustomSubmitButton buttonType={CustomSubmitButtonType.alternative} onButtonSubmit={searchOrthodonticReferralsToExcel}><FontAwesomeIcon icon={faFileExcel} /> Exportera till Excel</CustomSubmitButton>
                            <ClearFormButton />
                            <CustomSubmitButton>Sök</CustomSubmitButton>
                        </div>
                    </div>
                </div>
            </Form>
        </section>
    )
}